import { Stack, Typography } from '@mui/material';
import { Nationalities } from '@schooly/constants';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { $enum } from 'ts-enum-util';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { SelectSearchInput } from '../SelectSearchInput';
import { toggleMultipleValueArrayProperty } from '../utils';
import { NationalityTagSelect, NationalityTagSelectProps } from './NationalityTagSelect';

type NationalityExpandedSelectProps = PropsWithChildren<{
  selectedNationalities: Nationalities[];
  onApply: (v: Nationalities[]) => void;
  onClear: () => void;
  onClose: () => void;
  applyImmediate?: boolean;
}>;

const OPTIONS = $enum(Nationalities).map((value, key) => ({ value, label: key }));

export const NationalityExpandedSelect: FC<NationalityExpandedSelectProps> = ({
  selectedNationalities: initialSelectedNationalities,
  onApply,
  onClose,
  onClear,
  applyImmediate = false,
}) => {
  const [selectedNationalities, setSelectedNationalities] = useState<Nationalities[]>(
    initialSelectedNationalities,
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const toggleNationalities = useCallback(
    (v: Nationalities[]) => {
      setSelectedNationalities((nationalities) => {
        const newNationalities = toggleMultipleValueArrayProperty(nationalities, v);
        if (applyImmediate) {
          onApply(newNationalities);
        }
        return newNationalities;
      });
    },
    [applyImmediate, onApply],
  );

  const handleApply = useCallback(() => {
    onApply(selectedNationalities);
    onClose();
  }, [selectedNationalities, onApply, onClose]);

  const [query, setQuery] = useState('');
  const renderContent = useCallback(() => {
    const result = OPTIONS.filter(({ label }) =>
      label.toLowerCase().includes(query.toLowerCase()),
    ).map((v) => v.value);

    if (!result.length)
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );

    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderNationalityTags({
            nationalities: result,
            onClick: (nationality) => {
              toggleNationalities([nationality]);
              setQuery('');
            },
            tagProps: (nationality) => ({
              variant: selectedNationalities.includes(nationality) ? 'filled' : undefined,
            }),
          })}
        </Stack>
        {!applyImmediate && <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />}
      </Stack>
    );
  }, [handleApply, onClear, toggleNationalities, query, selectedNationalities, applyImmediate]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedNationalities.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
      onClickInputArea={() => inputRef.current?.focus()}
    >
      {renderNationalityTags({
        nationalities: selectedNationalities,
        onDelete: (nationality) => toggleNationalities([nationality]),
        tagProps: { size: 'small' },
      })}

      <SelectSearchInput ref={inputRef} autoFocus value={query} onChangeText={setQuery} />
    </ExpandedSelect>
  );
};

type RenderNationalityTagsParams = {
  nationalities: Nationalities[];
  onDelete?: (v: Nationalities) => void;
  onClick?: (v: Nationalities) => void;
  tagProps?:
    | Omit<NationalityTagSelectProps, 'nationality'>
    | ((v: Nationalities) => Omit<NationalityTagSelectProps, 'nationality'>);
};

export const renderNationalityTags = ({
  nationalities,
  onDelete,
  onClick,
  tagProps,
}: RenderNationalityTagsParams) =>
  nationalities.map((v) => (
    <NationalityTagSelect
      key={v}
      nationality={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
